import {
  DEFAULT_LOCALE,
  LOCALES,
  THEME_KEYS,
  type Locale,
  type ThemeKey,
} from "@/constants/config";
import { matchBrowserLocale } from "@bizy/core/i18n/resolve";
import {
  applyTheme,
  browserLanguages,
  readLocale,
  readSidebarCollapsed,
  readTheme,
  readTimezone,
  watchSystemTheme,
  writeLocale,
  writeSidebarCollapsed,
  writeTheme,
  writeTimezone,
} from "@/lib/preferences";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface PreferencesContextValue {
  theme: ThemeKey;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: ThemeKey) => void;
  locale: Locale;
  setLocale: (locale: Locale) => void;
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

const resolveTheme = (theme: ThemeKey): "light" | "dark" => {
  if (theme !== THEME_KEYS.system) return theme === THEME_KEYS.dark ? "dark" : "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

/** Theme, language, sidebar and timezone — persisted to localStorage (§6). */
export const PreferencesProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setThemeState] = useState<ThemeKey>(() => readTheme());
  const [locale, setLocaleState] = useState<Locale>(() => readLocale() ?? matchBrowserLocale(browserLanguages()));
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => readSidebarCollapsed());
  const [timezone, setTimezoneState] = useState<string>(() => readTimezone());
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() => resolveTheme(readTheme()));

  useEffect(() => {
    applyTheme(theme);
    setResolvedTheme(resolveTheme(theme));
    writeTheme(theme);
    if (theme !== THEME_KEYS.system) return;
    return watchSystemTheme(() => {
      applyTheme(theme);
      setResolvedTheme(resolveTheme(theme));
    });
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = locale === LOCALES.en ? "en" : "id";
    writeLocale(locale);
  }, [locale]);

  useEffect(() => {
    writeSidebarCollapsed(sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    writeTimezone(timezone);
  }, [timezone]);

  const toggleSidebar = useCallback(() => setSidebarCollapsed((value) => !value), []);

  const value = useMemo<PreferencesContextValue>(
    () => ({
      theme,
      resolvedTheme,
      setTheme: setThemeState,
      locale,
      setLocale: setLocaleState,
      sidebarCollapsed,
      toggleSidebar,
      timezone,
      setTimezone: setTimezoneState,
    }),
    [theme, resolvedTheme, locale, sidebarCollapsed, toggleSidebar, timezone],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = (): PreferencesContextValue => {
  const context = useContext(PreferencesContext);
  if (!context) throw new Error("usePreferences must be used inside PreferencesProvider.");
  return context;
};

export const DEFAULT_PREFERENCES_LOCALE = DEFAULT_LOCALE;
